import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Lightbulb, Battery, Sun, Moon, Zap, Shield } from 'lucide-react'

const Products = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const products = [
    {
      icon: Lightbulb,
      name: "Pigmentium™ Lighting Panels",
      description: "Revolutionary night-energy panels that store daylight and emit 500 Lux for 12+ hours.",
      features: ["5-second charging", "12-hour operation", "500 Lux brightness", "Zero electricity cost"],
      applications: ["Indoor lighting", "Emergency lighting", "Outdoor pathways", "Commercial spaces"]
    },
    {
      icon: Battery,
      name: "Pigmentium™ Energy Storage",
      description: "Advanced photoluminescent energy storage systems for various applications.",
      features: ["Instant charging", "Long-lasting glow", "Weather resistant", "Maintenance-free"],
      applications: ["Street lighting", "Safety signs", "Decorative lighting", "Industrial use"]
    },
    {
      icon: Sun,
      name: "Pigmentium™ Solar Integration",
      description: "Hybrid systems combining traditional solar with Pigmentium™ technology.",
      features: ["24/7 operation", "Extended efficiency", "Backup lighting", "Smart switching"],
      applications: ["Residential solar", "Commercial buildings", "Remote locations", "Grid support"]
    }
  ]

  return (
    <section id="products" className="py-20 bg-gradient-to-br from-blue-50 to-yellow-50">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Our <span className="text-yellow-500">Products</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover our revolutionary Pigmentium™ technology products that extend daylight into the night, 
            providing sustainable lighting solutions without electricity.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-yellow-200"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mr-4">
                  <product.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">{product.name}</h3>
              </div>
              
              <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Zap className="w-5 h-5 text-yellow-500 mr-2" />
                  Key Features
                </h4>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Shield className="w-5 h-5 text-blue-500 mr-2" />
                  Applications
                </h4>
                <div className="flex flex-wrap gap-2">
                  {product.applications.map((app, idx) => (
                    <span key={idx} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                      {app}
                    </span>
                  ))}
                </div>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full mt-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Request Quote
              </motion.button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-8 text-white">
            <Moon className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-3xl font-bold mb-4">Custom Solutions Available</h3>
            <p className="text-xl mb-6 opacity-90">
              Need a specific Pigmentium™ solution? We design and manufacture custom lighting systems 
              tailored to your exact requirements.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-orange-500 font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              Contact Our Engineers
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Products 